// src/components/examenes/ManageQuestionsModal.jsx
import { useEffect, useState, useCallback, useMemo } from "react";
import { useFuncion } from "../../../context/FuncionContext";
import ActivityQuestionsModal from "../../ActivityQuestionsModal";
import AIActivityQuestionsModal from "../../AIActivityQuestionsModal";
import CloneQuestionPicker from "./CloneQuestionPicker";
import { useCourseStructure } from "../../../context/CourseStructureContext";
import { useNavigate } from "react-router-dom";

function useDebouncedValue(value, delay = 200) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;

}
function flattenStructureNodes(nodes) {
  const out = [];

  const walk = (list) => {
    if (!Array.isArray(list)) return;
    for (const node of list) {
      if (!node || typeof node !== "object") continue;
      out.push(node);

      const children =
        node.children ||
        node.hijos ||
        node.items ||
        node.nodos ||
        node.subitems ||
        [];

      if (Array.isArray(children) && children.length) {
        walk(children);
      }
    }
  };

  walk(nodes);
  return out;
}
export default function ManageQuestionsModal({ exam, onClose }) {
  const {
    fetchExamQuestions,
    addCustomExamQuestion,
    cloneExamQuestion,
    deleteExamQuestion,
    updateExamQuestion,
    createExamQuestionPool,
    deleteExamRef,
    updateBankQuestion,
  } = useFuncion();

  const { toAbsoluteUrl, updateQuestion, getCourseStructure } = useCourseStructure();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [excludeIds, setExcludeIds] = useState([]);
  const [tab, setTab] = useState("list");
  const [questionsLocked, setQuestionsLocked] = useState(false);
  const [lockInfo, setLockInfo] = useState({ reasons: [], counts: {}, window: {} });

  const [uiNotice, setUiNotice] = useState(null);

  const showUiNotice = useCallback((notice) => {
    setUiNotice({
      id: Date.now(),
      type: "info",
      title: "Aviso",
      message: "",
      details: [],
      ...notice,
    });
  }, []);

  const closeUiNotice = useCallback(() => {
    setUiNotice(null);
  }, []);

  useEffect(() => {
    if (!uiNotice) return;

    const t = setTimeout(() => {
      setUiNotice(null);
    }, 9000);

    return () => clearTimeout(t);
  }, [uiNotice]);

  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [isAIBuilderOpen, setIsAIBuilderOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [resourceParentOptions, setResourceParentOptions] = useState([]);
  const [defaultResourceParentId, setDefaultResourceParentId] = useState(null);

  // =========================
  // BUSCADOR + PAGINACIÓN (LISTADO)
  // =========================
  const [listQuery, setListQuery] = useState("");
  const debouncedListQuery = useDebouncedValue(listQuery, 200);

  const LIST_PAGE_SIZES = [10, 25, 50, 100];
  const [listPageSize, setListPageSize] = useState(25);
  const [listPage, setListPage] = useState(1);

  // helper: normaliza para buscar
  const norm = (s) =>
    String(s ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  // ✅ Mostrar más/menos por fila (por defecto TODO contraído = mostrar menos)
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpanded = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // ===== Helpers de texto / UI =====
  // ===== Helpers de texto / UI =====
  const tN = (input, maxChars = 50) => {
    const s = String(input ?? "").replace(/\s+/g, " ").trim();
    if (!s) return "—";
    if (s.length <= maxChars) return s;
    return s.slice(0, Math.max(0, maxChars - 3)).trimEnd() + "...";
  };

  // compat si ya lo usabas en otras partes:
  const truncateText = (input, maxChars = 160) => tN(input, maxChars);


  const pluralize = (n, s, p = s + "s") => `${n} ${n === 1 ? s : p}`;
  const prettyFuente = (f) => {
    switch (String(f || "").toLowerCase()) {
      case "estructura_preguntas":
        return "Preguntas del curso";
      case "juego_item":
        return "Juego";
      case "banco_preguntas":
        return "Banco";
      default:
        return String(f || "—");
    }
  };

  const getQuestionOriginLabel = (q) => {
    if (q?.__kind === "ref") return "Desde banco";

    const originTokens = [
      q?.origen,
      q?.generated_by,
      q?.generado_por,
      q?.fuente_tabla,
      q?.source,
      q?.snapshot?.origen,
      q?.snapshot?.source,
      q?.snapshot?.generated_by,
      q?.snapshot?.generado_por,
      q?.opciones?._meta?.origen,
      q?.opciones?._meta?.source,
      q?.opciones?._meta?.generated_by,
      q?.opciones?._meta?.generado_por,
      q?.fuente,
    ]
      .map((value) => String(value || "").toLowerCase())
      .filter(Boolean);

    const isAiToken = (value) =>
      value === "ai" ||
      value === "ia" ||
      value === "source:ai" ||
      value === "generated_by_ai" ||
      value === "generado_por_ia" ||
      value.includes("creada con ia") ||
      value.includes("generada con ia") ||
      value.includes("inteligencia artificial");

    if (originTokens.some(isAiToken)) {
      return "Creada con IA";
    }
    if (originTokens.some((value) => value.includes("archivo"))) {
      return "Desde archivo";
    }
    if (originTokens.some((value) => value.includes("banco_preguntas"))) {
      return "Desde banco";
    }
    return "Manual";
  };

  const prettyTipoNodo = (t) => {
    switch (String(t || "").toLowerCase()) {
      case "nivel":
        return "Nivel";
      case "modulo":
        return "Módulo";
      case "componente":
        return "Componente";
      case "tarea":
        return "Tarea";
      default:
        return t || "";
    }
  };

  const prettyTipoPregunta = (t) => {
    const k = String(t || "").toLowerCase();
    switch (k) {
      case "opcion_multiple":
      case "multiple_choice":
        return "Opción múltiple";
      case "seleccion_multiple":
      case "multiple_select":
        return "Selección múltiple";
      case "verdadero_falso":
      case "vf":
      case "true_false":
        return "Verdadero/Falso";
      case "respuesta_corta":
      case "short_answer":
        return "Respuesta corta";
      case "emparejamiento":
      case "matching":
        return "Emparejamiento";
      case "pool":
        return "Aleatorias";
      default:
        return t || "—";
    }
  };

  // ===== Normalizador: mezcla custom + refs, usando preview ref_* cuando llega =====
  const normalizeQuestions = (resp) => {
    if (Array.isArray(resp)) return resp.map((q) => ({ ...q, __kind: "custom" }));

    const out = [];
    const custom = Array.isArray(resp?.custom) ? resp.custom : [];
    const refs = Array.isArray(resp?.refs) ? resp.refs : [];

    const primaryScopeTag = (r) => {
      const t = r?.estructura_tipo ? prettyTipoNodo(r.estructura_tipo) : null;
      const o = r?.orden ?? r?.estructura_orden;
      if (t && Number.isFinite(Number(o))) return `(${t} ${o})`;
      const nm = r?.estructura_nombre;
      return nm ? `(${nm})` : "";
    };

    for (const q of custom) out.push({ ...q, __kind: "custom" });

    for (const r of refs) {
      const isPool = r.modo === "pool";
      const hasPreview = r.modo === "single" && r.ref_pregunta;

      if (hasPreview) {
        out.push({
          id: `ref-${r.id}`,
          ref_id: r.id,
          __kind: "ref",
          pregunta: r.ref_pregunta,
          tipo_pregunta: r.ref_tipo_pregunta,
          cantidad: 1, // ✅ individual siempre 1
          fuente: "Referenciada",
          orden: r.orden ?? null,
          __raw: r,
          __preview: {
            opciones: r.ref_opciones,
            respuesta_correcta: r.ref_respuesta_correcta,
            explicacion: r.ref_explicacion,
          },
        });
        continue;
      }

      const fuenteBonita = prettyFuente(r.fuente);
      const cant = Number(r.cantidad || 0);

      const preguntaTxt = isPool
        ? `${pluralize(cant, "pregunta")} aleatoria${cant === 1 ? "" : "s"}`
        : `REF ${fuenteBonita}#${r.fuente_id}`;

      out.push({
        id: `ref-${r.id}`,
        ref_id: r.id,
        __kind: "ref",
        pregunta: tN(preguntaTxt, 50),
        tipo_pregunta: isPool ? "pool" : "single",
        cantidad: isPool ? cant : 1, // ✅ pool igual / single = 1
        fuente: "Referenciada",
        orden: r.orden ?? null,
        __raw: r,
      });
    }

    out.sort(
      (a, b) => (a.orden ?? 1e9) - (b.orden ?? 1e9) || String(a.id).localeCompare(String(b.id))
    );

    return out;
  };

  const isEditableRefRow = (q) => {
    if (q?.__kind !== "ref") return false;
    const modo = String(q?.__raw?.modo || "").toLowerCase();
    // solo "single" es editable (pool no)
    return modo === "single";
  };

  const buildRefEditQuestion = (row) => {
    const r = row?.__raw || {};
    const fuente = r?.fuente || null;
    const fuente_id = r?.fuente_id ?? null;
    const bank_id = r?.bank_id ?? r?.banco_id ?? r?.bankId ?? null;

    // si tenemos preview (ref_pregunta / ref_opciones / etc)
    const pregunta = row?.pregunta || r?.ref_pregunta || "";
    const tipo_pregunta = row?.tipo_pregunta || r?.ref_tipo_pregunta || "seleccion_multiple";

    const opciones = row?.__preview?.opciones ?? r?.ref_opciones ?? {};
    const respuesta_correcta = row?.__preview?.respuesta_correcta ?? r?.ref_respuesta_correcta ?? {};
    const explicacion = row?.__preview?.explicacion ?? r?.ref_explicacion ?? "";

    // Nota: el modal lee `pregunta/enunciado`, `tipo_pregunta`, etc.
    return {
      // id del modal (no importa mucho, pero útil)
      id: `source-${fuente}-${fuente_id}`,

      pregunta,
      tipo_pregunta,
      opciones,
      respuesta_correcta,
      explicacion,
      puntos: Number(r?.ref_puntos ?? 1),
      tiempo_limite_segundos: r?.ref_tiempo_limite_segundos ?? null,

      // ✅ banderas internas para saber que esto NO es custom del examen
      __editMode: "ref_source",
      __editTarget: { fuente, fuente_id, bank_id },
    };
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErr(null);

      const resp = await fetchExamQuestions(exam.id);

      const poolReconciliation = resp?.meta?.poolReconciliation;

      if (
        poolReconciliation &&
        (
          poolReconciliation.adjusted?.length > 0 ||
          poolReconciliation.removed?.length > 0
        )
      ) {
        const msg = [
          ...(poolReconciliation.warnings || []),
        ].join("\n");

        if (msg) {
          showUiNotice({
            type: poolReconciliation.removed?.length > 0 ? "warning" : "info",
            title: "Preguntas aleatorias ajustadas",
            message:
              "Se actualizó la configuración porque algunas preguntas del bloque ya no están disponibles o la cantidad solicitada superaba las preguntas existentes.",
            details: poolReconciliation.warnings || [],
          });
        }
      }

      // ✅ lock meta desde /schedules/:horarioId/questions (misma ruta)
      const meta = resp?.meta || {};
      setQuestionsLocked(!!meta.questionsLocked);
      setLockInfo({
        reasons: meta.lockReasons || [],
        counts: meta.lockCounts || {},
        window: meta.lockWindow || {},
      });

      const normalized = normalizeQuestions(resp);
      setQuestions(normalized);

      // ✅ por defecto todos en "Mostrar menos"
      setExpanded(new Set());

      // Evitar duplicados en el picker (estructura_preguntas ya usada)
      const exIds = [];
      if (Array.isArray(resp?.custom)) {
        resp.custom.forEach((q) => {
          if (q.fuente === "estructura_preguntas" && q.fuente_id) exIds.push(Number(q.fuente_id));
        });
      }
      if (Array.isArray(resp?.refs)) {
        resp.refs.forEach((r) => {
          if (r.modo === "single" && r.fuente === "estructura_preguntas" && r.fuente_id)
            exIds.push(Number(r.fuente_id));
        });
      }
      setExcludeIds([...new Set(exIds)]);
    } catch (e) {
      setErr(e?.message || "No se pudieron cargar las preguntas");
      setQuestions([]);
      setExcludeIds([]);
      setExpanded(new Set());
    } finally {
      setLoading(false);
    }
  }, [exam.id, fetchExamQuestions, showUiNotice]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const structure = await getCourseStructure(exam.curso_id);
        if (!mounted) return;

        const rootNodes = Array.isArray(structure) ? structure : [];
        const allNodes = flattenStructureNodes(rootNodes);

        const resourceNodes = allNodes;

        const seen = new Set();
        const options = resourceNodes
          .map((n) => {
            const id = Number(n?.id);
            if (!Number.isFinite(id) || id <= 0 || seen.has(id)) return null;
            seen.add(id);

            return {
              id,
              nombre: n?.nombre || `Apartado ${n?.orden ?? id}`,
              label: `${String(n?.tipo || "apartado").toUpperCase()} - ${n?.nombre || "Sin nombre"}${n?.orden != null ? ` · Orden ${n.orden}` : ""}`,
            };
          })
          .filter(Boolean);

        setResourceParentOptions(options);
        setDefaultResourceParentId((prev) => {
          const prevValid = options.some((opt) => opt.id === prev);
          return prevValid ? prev : (options[0]?.id ?? null);
        });
      } catch {
        if (!mounted) return;
        setResourceParentOptions([]);
        setDefaultResourceParentId(null);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [exam.curso_id, getCourseStructure]);

  // ===== Builder submit (✅ quitamos dificultad) =====
  const handleBuilderSubmit = async (builderPayload) => {
    // ✅ SI estamos editando una pregunta referenciada => editamos la FUENTE
    if (selectedQuestion?.__editMode === "ref_source") {
      const tgt = selectedQuestion?.__editTarget || {};
      const fuente = String(tgt?.fuente || "").toLowerCase();
      const fuenteId = Number(tgt?.fuente_id);

      if (!Number.isFinite(fuenteId) || fuenteId <= 0) {
        alert("No se pudo editar: fuente_id inválido.");
        return;
      }

      try {
        if (fuente === "estructura_preguntas") {
          if (!updateQuestion) {
            alert("Falta implementar updateQuestion en useCourseStructure.");
            return;
          }
          // Aquí enviamos el payload completo del modal (incluye opciones/respuesta/media_*)
          await updateQuestion(fuenteId, builderPayload);
        } else if (fuente === "banco_preguntas") {
          if (!updateBankQuestion) {
            alert("Falta implementar updateBankQuestion en useFuncion.");
            return;
          }
          const bankId = Number(tgt?.bank_id);
          if (!Number.isFinite(bankId) || bankId <= 0) {
            alert("No se pudo editar: falta bank_id en la referencia.");
            return;
          }
          const cursoIdSafe = Number(exam?.curso_id ?? exam?.cursoId ?? 0);
          if (!Number.isFinite(cursoIdSafe) || cursoIdSafe <= 0) {
            alert("No se pudo editar: falta curso_id.");
            return;
          }

          await updateBankQuestion(cursoIdSafe, bankId, fuenteId, builderPayload);
        } else {
          alert(`No se puede editar esta fuente: ${tgt?.fuente || "desconocida"}`);
          return;
        }

        await load();
        setIsBuilderOpen(false);
        setSelectedQuestion(null);
        setTab("list");
        return;
      } catch (e) {
        alert(e?.message || "No se pudo editar la pregunta fuente");
        return;
      }
    }

    // ✅ Caso normal: custom del examen (lo que ya tenías)
    const payload = {
      pregunta: builderPayload.pregunta ?? builderPayload.nombre,
      tipo_pregunta: builderPayload.tipo_pregunta,
      opciones: builderPayload.opciones || {},
      respuesta_correcta: builderPayload.respuesta_correcta || {},
      explicacion: builderPayload.explicacion || null,
      puntos: Number(builderPayload.puntos || 1),
      orden: builderPayload.orden ?? null,
    };

    if (selectedQuestion?.id) {
      await updateExamQuestion(exam.id, selectedQuestion.id, payload);
    } else {
      await addCustomExamQuestion(exam.id, payload);
    }

    await load();
    setIsBuilderOpen(false);
    setSelectedQuestion(null);
    setTab("list");
  };

  const handleSaveQuestionAI = async (payload) => {
    return await addCustomExamQuestion(exam.id, payload);
  };

  const handleDeleteGeneratedQuestionAI = async (question) => {
    const raw = question?.id ?? question?.question_id ?? question?.horario_examen_pregunta_id;
    const realId = parseInt(String(raw).replace('pregunta-', ''), 10);
    if (Number.isNaN(realId)) {
      throw new Error('ID de pregunta inválido');
    }
    await deleteExamQuestion(exam.id, realId);
    await load();
  };

  const remove = async (row) => {
    if (row.__kind === "custom") {
      if (!window.confirm("¿Eliminar esta pregunta del examen?")) return;
      await deleteExamQuestion(exam.id, row.id);
    } else {
      if (!window.confirm("¿Eliminar esta referencia del examen?")) return;
      await deleteExamRef(exam.id, row.ref_id);
    }
    await load();
  };

  const onEdit = (q) => {
    if (q.__kind !== "custom") return;
    setSelectedQuestion({ ...q, nombre: q.pregunta });
    setIsBuilderOpen(true);
  };

  // ===== Preview de opciones (SOLO cuando está en "Mostrar más") =====
  const renderOptionPreview = (q) => {
    const opciones = q.__preview?.opciones ?? q.opciones;
    const rcRaw = q.__preview?.respuesta_correcta ?? q.respuesta_correcta;
    const tipo = String(q.tipo_pregunta || "").toLowerCase();

    const mediaUrlRaw =
      opciones?.media?.url || opciones?.media?.URL || opciones?.media?.src || null;
    const mediaSrc = mediaUrlRaw ? toAbsoluteUrl(mediaUrlRaw) : null;

    const mediaBlock = mediaSrc ? (
      <div style={{ marginTop: 10 }}>
        <img
          src={mediaSrc}
          alt={opciones?.media?.alt || "media"}
          style={{
            width: "min(420px, 100%)",
            borderRadius: 12,
            display: "block",
            border: "1px solid rgba(226,232,240,.9)",
          }}
        />
      </div>
    ) : null;

    // ---------- helpers: normalización robusta de correctas ----------
    const normStr = (s) =>
      String(s ?? "")
        .trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

    const normalizeOptionItems = (opciones) => {
      if (!opciones) return [];

      if (Array.isArray(opciones)) {
        return opciones.map((o, i) => {
          if (typeof o === "string" || typeof o === "number") {
            return { id: String(i), texto: String(o) };
          }
          return {
            id: String(o?.id ?? i),
            texto: String(o?.texto ?? o?.label ?? o?.value ?? o?.name ?? `Opción ${i + 1}`),
            correcta: o?.correcta,
          };
        });
      }

      if (Array.isArray(opciones?.opciones)) {
        return opciones.opciones.map((o, i) => ({
          id: String(o?.id ?? i),
          texto: String(o?.texto ?? o?.label ?? o?.value ?? o?.name ?? `Opción ${i + 1}`),
          correcta: o?.correcta,
        }));
      }

      if (typeof opciones === "object") {
        const vals = Object.values(opciones);
        if (Array.isArray(vals) && vals.length) {
          return vals.map((o, i) => ({
            id: String(o?.id ?? i),
            texto: String(o?.texto ?? o?.label ?? o?.value ?? o?.name ?? `Opción ${i + 1}`),
            correcta: o?.correcta,
          }));
        }
      }

      return [];
    };

    const pickRCValues = (raw) => {
      if (raw == null) return [];
      if (Array.isArray(raw)) return raw;

      if (typeof raw === "object") {
        if (Array.isArray(raw.correctas)) return raw.correctas;
        if (Array.isArray(raw.ids)) return raw.ids;
        if (Array.isArray(raw.indices)) return raw.indices;
        if (Array.isArray(raw.respuestas)) return raw.respuestas;

        if ("respuesta" in raw) return [raw.respuesta];
        if ("valor" in raw) return [raw.valor];
        if ("value" in raw) return [raw.value];
        if ("id" in raw) return [raw.id];

        return [];
      }

      return [raw];
    };

    const mapToOptionId = (val, items) => {
      if (val == null) return null;

      const s = String(val).trim();
      if (!s) return null;

      const ids = new Set(items.map((it) => String(it.id)));
      if (ids.has(s)) return s;

      const n = Number(s);
      if (Number.isFinite(n)) {
        if (n >= 1 && n <= items.length) return String(items[n - 1]?.id); // 1..N
        if (n >= 0 && n < items.length) return String(items[n]?.id);      // 0..N-1
      }

      const ns = normStr(s);
      const hit = items.find((it) => normStr(it.texto) === ns);
      if (hit) return String(hit.id);

      return null;
    };

    const computeCorrectSet = (tipo, opciones, rcRaw) => {
      const t = String(tipo || "").toLowerCase();

      const isMulti =
        t === "seleccion_multiple" ||
        t === "multiple_select" ||
        t === "multiple_answer";

      const isSingle =
        t === "opcion_multiple" ||
        t === "multiple_choice" ||
        t === "seleccion_simple" ||
        t === "single_choice";

      const items = normalizeOptionItems(opciones);

      // PRIORIDAD 1: flags correcta en opciones
      const flagged = items.filter((it) => it?.correcta === true);
      if (flagged.length) {
        const ids = flagged.map((it) => String(it.id));
        return new Set(isSingle ? [ids[0]] : ids);
      }

      // PRIORIDAD 2: respuesta_correcta
      const vals = pickRCValues(rcRaw);
      const mapped = vals.map((v) => mapToOptionId(v, items)).filter(Boolean);
      const uniq = Array.from(new Set(mapped));

      if (isSingle) return new Set(uniq.slice(0, 1));
      if (!isMulti && uniq.length > 1) return new Set([uniq[0]]);
      return new Set(uniq);
    };

    // ✅ rcVals ahora sí existe
    const rcVals = pickRCValues(rcRaw);

    // Respuesta corta
    if (tipo === "respuesta_corta" || tipo === "short_answer") {
      const expected = rcVals?.[0] ?? "";
      return (
        <>
          {mediaBlock}
          {expected ? (
            <div style={ST.answerBox}>
              <span style={ST.answerLabel}>Respuesta</span>
              <span style={ST.answerValue}>{String(expected)}</span>
            </div>
          ) : null}
        </>
      );
    }

    // VF
    if (
      (!opciones || (Array.isArray(opciones) && opciones.length === 0)) &&
      (tipo === "verdadero_falso" || tipo === "vf" || tipo === "true_false")
    ) {
      const itemsVF = ["Verdadero", "Falso"];
      const truthy = ["v", "verdadero", "true", true, 1, "1"];
      const isTrue = rcVals.some((v) =>
        truthy.includes(typeof v === "string" ? v.toLowerCase() : v)
      );

      return (
        <>
          {mediaBlock}
          <div style={ST.optsWrap}>
            {itemsVF.map((label, idx) => {
              const ok = isTrue ? idx === 0 : idx === 1;
              return (
                <span key={idx} style={{ ...ST.optPill, ...(ok ? ST.optPillOk : {}) }}>
                  {label} {ok ? "✅" : ""}
                </span>
              );
            })}
          </div>
        </>
      );
    }

    if (!opciones) return mediaBlock;

    // ✅ NO vuelvas a pisar items: aquí ya viene uniforme
    const items = normalizeOptionItems(opciones);
    if (!items.length) return mediaBlock;

    const correctSet = computeCorrectSet(tipo, opciones, rcRaw);
    const sliceN = 12;

    return (
      <>
        {mediaBlock}
        <div style={ST.optsWrap}>
          {items.slice(0, sliceN).map((opt, idx) => {
            const id = String(opt?.id ?? idx);
            const label = opt?.texto ?? `Opción ${idx + 1}`;
            const ok = correctSet.has(id);

            return (
              <span key={`${q.id}-${id}`} style={{ ...ST.optPill, ...(ok ? ST.optPillOk : {}) }}>
                {tN(String(label), 50)} {ok ? "✅" : ""}
              </span>
            );
          })}
          {items.length > sliceN ? (
            <span style={ST.morePill}>+{items.length - sliceN} más</span>
          ) : null}
        </div>
      </>
    );
  };

  const handlePickerConfirm = async (payload) => {
    try {
      if (payload?.mode === "pool") {
        const createdPool = await createExamQuestionPool(exam.id, payload);

        if (createdPool?.adjusted) {
          showUiNotice({
            type: "warning",
            title: "Cantidad ajustada automáticamente",
            message:
              createdPool.warning ||
              `La cantidad fue ajustada automáticamente a ${createdPool.cantidad_ajustada}.`,
            details: [
              `Solicitadas: ${createdPool.cantidad_original_solicitada ?? "—"}`,
              `Disponibles: ${createdPool.preguntas_disponibles ?? "—"}`,
              `Configuradas: ${createdPool.cantidad_ajustada ?? createdPool.cantidad ?? "—"}`,
            ],
          });
        }
      } else {
        const { fuente, ids } = payload;

        await Promise.all(
          ids.map((id) =>
            cloneExamQuestion(exam.id, {
              fuente,
              fuente_id: id,
              ...(fuente === "banco_preguntas" && payload.bank_id
                ? { bank_id: payload.bank_id }
                : {}),
            })
          )
        );
      }

      await load();
      setIsPickerOpen(false);
      setTab("list");
    } catch (e) {
      showUiNotice({
        type: "error",
        title: "No se pudo completar la acción",
        message: e?.message || "No se pudieron clonar las preguntas seleccionadas",
      });
    }
  };

  const cursoIdSafe = exam?.curso_id ?? exam?.cursoId ?? null;
  const cursoTipoSafe = exam?.curso_tipo ?? exam?.cursoTipo ?? null;

  const getQuestionEffectiveCount = (q) => {
    if (!q) return 0;

    const kind = String(q?.__kind || "").toLowerCase();
    const modo = String(q?.__raw?.modo || q?.modo || "").toLowerCase();
    const tipo = String(q?.tipo_pregunta || "").toLowerCase();

    const rawCantidad = Number(
      q?.cantidad ??
      q?.__raw?.cantidad ??
      q?.total_preguntas ??
      q?.preguntas_count ??
      0
    );

    // Bloques aleatorios / pools: una fila visual puede representar N preguntas.
    if (
      kind === "ref" &&
      (
        modo === "pool" ||
        tipo === "pool" ||
        tipo === "aleatorias" ||
        rawCantidad > 1
      )
    ) {
      return Math.max(0, rawCantidad || 0);
    }

    // Referencia individual o pregunta manual: cuenta como 1.
    return 1;
  };

  const requiredQuestions = Number(exam?.numero_preguntas || exam?.required_questions || 40);

  const registeredQuestions = useMemo(() => {
    return (questions || []).reduce((acc, q) => {
      return acc + getQuestionEffectiveCount(q);
    }, 0);
  }, [questions]);

  const missingQuestions = Math.max(0, requiredQuestions - registeredQuestions);

  const totalCountLabel = useMemo(() => {
    if (loading) return "Cargando…";
    return `${registeredQuestions} pregunta${registeredQuestions === 1 ? "" : "s"}`;
  }, [registeredQuestions, loading]);

  const questionOriginSummary = useMemo(() => {
    const summary = {
      ai: 0,
      banco: 0,
      archivo: 0,
      manual: 0,
      referenciada: 0,
    };

    for (const q of questions || []) {
      if (q?.__kind === "ref") {
        summary.referenciada += Number(q?.cantidad || 1);
        continue;
      }

      const fuente = String(q?.fuente || "").toLowerCase();

      if (fuente.includes("banco")) summary.banco += 1;
      else if (fuente.includes("archivo")) summary.archivo += 1;
      else if (fuente.includes("ia") || fuente.includes("ai")) summary.ai += 1;
      else summary.manual += 1;
    }

    return summary;
  }, [questions]);

  // =========================
  // LISTA FILTRADA + PAGINADA
  // =========================
  const filteredQuestions = useMemo(() => {
    const q = norm(debouncedListQuery);
    if (!q) return questions;

    return (questions || []).filter((row) => {
      const txt = [
        row?.pregunta,
        prettyTipoPregunta(row?.tipo_pregunta),
        row?.__kind === "custom" ? prettyFuente(row?.fuente) : row?.fuente,
        row?.__raw?.estructura_tipo ? prettyTipoNodo(row.__raw.estructura_tipo) : "",
        row?.__raw?.fuente ? prettyFuente(row.__raw.fuente) : "",
        row?.__raw?.fuente_id != null ? String(row.__raw.fuente_id) : "",
        row?.orden != null ? String(row.orden) : "",
      ]
        .filter(Boolean)
        .join(" | ");

      return norm(txt).includes(q);
    });
  }, [questions, debouncedListQuery]);

  const listTotal = filteredQuestions.length;

  const listTotalPages = useMemo(() => {
    const ps = Number(listPageSize) || 25;
    return Math.max(1, Math.ceil(listTotal / ps));
  }, [listTotal, listPageSize]);

  // si cambias búsqueda/tamaño, regresa a página 1
  useEffect(() => {
    setListPage(1);
  }, [debouncedListQuery, listPageSize]);

  useEffect(() => {
    setExpanded(new Set());
  }, [debouncedListQuery, listPage, listPageSize]);

  // clamp cuando cambia total
  useEffect(() => {
    setListPage((p) => Math.min(Math.max(1, p), listTotalPages));
  }, [listTotalPages]);

  const pagedQuestions = useMemo(() => {
    const ps = Number(listPageSize) || 25;
    const start = (Math.max(1, listPage) - 1) * ps;
    return filteredQuestions.slice(start, start + ps);
  }, [filteredQuestions, listPage, listPageSize]);

  const listRangeLabel = useMemo(() => {
    if (!listTotal) return "0";
    const ps = Number(listPageSize) || 25;
    const start = (Math.max(1, listPage) - 1) * ps + 1;
    const end = Math.min(listTotal, start + ps - 1);
    return `${start}-${end} de ${listTotal}`;
  }, [listTotal, listPage, listPageSize]);

  const goListPage = (p) => setListPage(Math.min(Math.max(1, p), listTotalPages));

  return (
    <div style={ST.overlay}>
      <style>{CSS}</style>

      <div style={ST.modal}>
        <div style={ST.heroHeader}>
          <div style={ST.heroHeaderTop}>
            <div style={ST.heroIconWrap}>
              <div style={ST.heroIcon}>📄</div>

              <div style={{ minWidth: 0 }}>
                <div style={ST.title}>Gestión de Preguntas</div>

                <div style={ST.heroMetaRow}>
                  <span>{String(exam?.fecha_examen || "").slice(0, 10) || "—"}</span>
                  <span style={ST.metaTimeChip}>
                    {exam?.hora_inicio || "—"} - {exam?.hora_fin || "—"}
                  </span>
                </div>

                <div style={ST.heroStatsRow}>
                  <span style={ST.heroMiniStat}>
                    Registradas: <b>{registeredQuestions}</b>
                  </span>
                  <span style={ST.heroMiniStat}>
                    Requeridas: <b>{requiredQuestions}</b>
                  </span>
                </div>
              </div>
            </div>

            <button onClick={onClose} style={ST.closeGhostBtn}>
              ✕
            </button>
          </div>

          {missingQuestions > 0 && (
            <div style={ST.warningBanner}>
              <div style={ST.warningBannerTitle}>Preguntas insuficientes</div>
              <div style={ST.warningBannerText}>
                Deben existir al menos {requiredQuestions} preguntas para que el examen pueda ejecutarse.
              </div>
            </div>
          )}

          {questionsLocked && (
            <div style={ST.lockBanner}>
              🔒 Este horario ya tiene presentaciones de estudiantes o ya inició. Solo lectura.
            </div>
          )}

          {uiNotice && (
            <div
              style={{
                ...ST.noticeBanner,
                ...(uiNotice.type === "success" ? ST.noticeSuccess : {}),
                ...(uiNotice.type === "warning" ? ST.noticeWarning : {}),
                ...(uiNotice.type === "error" ? ST.noticeError : {}),
                ...(uiNotice.type === "info" ? ST.noticeInfo : {}),
              }}
            >
              <div style={ST.noticeIcon}>
                {uiNotice.type === "success"
                  ? "✅"
                  : uiNotice.type === "warning"
                    ? "⚠️"
                    : uiNotice.type === "error"
                      ? "❌"
                      : "ℹ️"}
              </div>

              <div style={ST.noticeBody}>
                <div style={ST.noticeTitle}>{uiNotice.title}</div>

                {uiNotice.message ? (
                  <div style={ST.noticeText}>{uiNotice.message}</div>
                ) : null}

                {Array.isArray(uiNotice.details) && uiNotice.details.length > 0 ? (
                  <ul style={ST.noticeList}>
                    {uiNotice.details.map((d, idx) => (
                      <li key={`${uiNotice.id}-${idx}`}>{d}</li>
                    ))}
                  </ul>
                ) : null}
              </div>

              <button
                type="button"
                onClick={closeUiNotice}
                style={ST.noticeClose}
                title="Cerrar aviso"
              >
                ✕
              </button>
            </div>
          )}
        </div>

        <div style={ST.cleanTabsRow}>
          <button
            onClick={() => setTab("list")}
            style={{ ...ST.cleanTab, ...(tab === "list" ? ST.cleanTabActive : {}) }}
          >
            ≡ Listado
          </button>

          <button
            onClick={() => !questionsLocked && setTab("add")}
            disabled={questionsLocked}
            style={{
              ...ST.cleanTab,
              ...(tab === "add" ? ST.cleanTabActive : {}),
              ...(questionsLocked ? { opacity: 0.55, cursor: "not-allowed" } : {}),
            }}
          >
            ＋ Agregar
          </button>
        </div>

        <div style={ST.content}>
          {tab === "list" && (
            <div style={ST.panel}>
              <div style={ST.panelHeaderRedesigned}>
                <div style={ST.searchWrapWide}>
                  <span style={ST.searchIcon}>🔎</span>
                  <input
                    value={listQuery}
                    onChange={(e) => setListQuery(e.target.value)}
                    placeholder="Buscar por tipo o detalles..."
                    style={ST.searchInputWide}
                  />
                </div>

                <select
                  value={listPageSize}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    setListPageSize(LIST_PAGE_SIZES.includes(n) ? n : 25);
                  }}
                  style={ST.filterSelect}
                >
                  {LIST_PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>
                      Mostrar {n}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={ST.table}>
                  <thead>
                    <tr>
                      <th style={{ ...ST.th, width: 180 }}>ORIGEN</th>
                      <th style={{ ...ST.th, width: 110, textAlign: "center" }}>CANTIDAD</th>
                      <th style={{ ...ST.th, width: 200 }}>TIPO DE PREGUNTA</th>
                      <th style={{ ...ST.th }}>DETALLES</th>
                      <th style={{ ...ST.th, width: 160, textAlign: "center" }}>ACCIONES</th>
                    </tr>
                  </thead>

                  <tbody>
                    {pagedQuestions.map((q) => {
                      const isPool = String(q.tipo_pregunta).toLowerCase() === "pool";
                      const isExpanded = expanded.has(q.id);
                      const hasDetails = !!(q.__preview || q.opciones || q.__preview?.explicacion || q.explicacion);

                      const originLabel = getQuestionOriginLabel(q);

                      const originStyle =
                        originLabel === "Creada con IA"
                          ? ST.originAi
                          : originLabel === "Desde banco"
                            ? ST.originBanco
                            : originLabel === "Desde archivo"
                              ? ST.originArchivo
                              : ST.originManual;

                      return (
                        <tr key={q.id} style={ST.tr}>
                          <td style={ST.td}>
                            <span style={{ ...ST.originPillTable, ...originStyle }}>
                              {originLabel}
                            </span>
                          </td>

                          <td style={{ ...ST.td, textAlign: "center" }}>
                            <b style={{ fontSize: 22, color: "var(--text)" }}>
                              {isPool ? q.cantidad ?? q.__raw?.cantidad ?? "—" : 1}
                            </b>
                          </td>

                          <td style={ST.td}>
                            <span style={isPool ? ST.typePillPool : ST.typePill}>
                              {prettyTipoPregunta(q.tipo_pregunta)}
                            </span>
                            {isPool ? (
                              <div style={ST.typeHint}>No aplica vista tipo en bloques</div>
                            ) : null}
                          </td>

                          <td style={ST.td}>
                            <div style={ST.qTitleExpanded}>
                              {isExpanded ? String(q.pregunta ?? "—") : tN(q.pregunta, 70)}
                            </div>

                            {isExpanded && (q.__preview || q.opciones) ? (
                              <div style={{ marginTop: 10 }}>{renderOptionPreview(q)}</div>
                            ) : null}

                            {isExpanded && (q.__preview?.explicacion || q.explicacion) ? (
                              <div style={ST.explBox}>
                                <div style={ST.explLabel}>Explicación</div>
                                <div style={ST.explText}>
                                  {tN(String(q.__preview?.explicacion || q.explicacion), 90)}
                                </div>
                              </div>
                            ) : null}
                          </td>

                          <td style={{ ...ST.td, textAlign: "center" }}>
                            <div style={ST.iconActions}>
                              <button
                                onClick={() => toggleExpanded(q.id)}
                                style={ST.iconBtn}
                                title={isExpanded ? "Ocultar detalles" : "Ver detalles"}
                              >
                                👁
                              </button>

                              {q.__kind === "custom" ? (
                                <>
                                  <button onClick={() => onEdit(q)} style={ST.iconBtn} disabled={questionsLocked} title="Editar">
                                    ✎
                                  </button>
                                  <button onClick={() => remove(q)} style={ST.iconBtnDanger} disabled={questionsLocked} title="Eliminar">
                                    🗑
                                  </button>
                                </>
                              ) : (
                                <button onClick={() => remove(q)} style={ST.iconBtnDanger} disabled={questionsLocked} title="Eliminar">
                                  🗑
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                    {!pagedQuestions.length && !loading && (
                      <tr>
                        <td colSpan={5} style={ST.emptyCell}>
                          {listQuery.trim()
                            ? `No hay resultados para “${listQuery.trim()}”.`
                            : "No hay preguntas en este examen"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div style={ST.paginationBar}>
                <div style={ST.mutedSmall}>
                  Página <b style={{ color: "var(--text)" }}>{listPage}</b> de{" "}
                  <b style={{ color: "var(--text)" }}>{listTotalPages}</b>
                </div>

                <div style={ST.pagerBtns}>
                  <button style={ST.pagerBtn} onClick={() => goListPage(listPage - 1)} disabled={listPage <= 1}>
                    ←
                  </button>
                  <button style={ST.pagerBtn} onClick={() => goListPage(listPage + 1)} disabled={listPage >= listTotalPages}>
                    →
                  </button>
                </div>
              </div>
            </div>
          )}

          {tab === "add" && (
            <div style={ST.addGridRedesigned}>
              <div style={ST.addCard}>
                <div style={ST.addCardIconBlue}>✎</div>
                <div style={ST.addCardTitle}>Creación Manual</div>
                <div style={ST.addCardDesc}>
                  Redacta preguntas de opción múltiple, única o verdadero/falso una a una.
                </div>
                <button
                  onClick={() => {
                    setSelectedQuestion(null);
                    setIsBuilderOpen(true);
                  }}
                  style={ST.addCardBtnBlue}
                  disabled={questionsLocked}
                >
                  Crear Pregunta
                </button>
              </div>

              <div style={ST.addCard}>
                <div style={ST.addCardIconPurple}>✦</div>
                <div style={ST.addCardTitle}>Generar con IA</div>
                <div style={ST.addCardDesc}>
                  Proporciona un tema o documento y nuestra IA generará las preguntas por ti.
                </div>
                <button
                  onClick={() => {
                    setSelectedQuestion(null);
                    setIsBuilderOpen(false);
                    setIsAIBuilderOpen(true);
                  }}
                  style={ST.addCardBtnPurple}
                  disabled={questionsLocked}
                >
                  Usar Inteligencia Artificial
                </button>
              </div>

              <div style={ST.addCard}>
                <div style={ST.addCardIconGreen}>▥</div>
                <div style={ST.addCardTitle}>Preguntas Existentes</div>
                <div style={ST.addCardDesc}>
                  {cursoTipoSafe === "examen_solo" ? "Selecciona preguntas específicas o un bloque aleatorio desde un banco existente." : "Elige si quieres importar desde el banco de preguntas o desde preguntas ya creadas en el curso."}
                </div>
                <button
                  onClick={() => setIsPickerOpen(true)}
                  style={ST.addCardBtnGreen}
                  disabled={questionsLocked}
                >
                  Elegir fuente de preguntas
                </button>
              </div>

              <div style={ST.addCard}>
                <div style={ST.addCardIconOrange}>⇪</div>
                <div style={ST.addCardTitle}>Importar desde Archivo</div>
                <div style={ST.addCardDesc}>
                  Carga un archivo local.
                </div>
                <div style={ST.addCardNote}>Formatos compatibles: .xml, .txt (Aiken)</div>
                <button
                  onClick={() => {
                    setSelectedQuestion(null);
                    setIsBuilderOpen(false);
                    setIsAIBuilderOpen(true);
                  }}
                  style={ST.addCardBtnOrange}
                  disabled={questionsLocked}
                >
                  Buscar archivo
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ActivityQuestionsModal
        isOpen={isBuilderOpen}
        onClose={() => {
          setIsBuilderOpen(false);
          setSelectedQuestion(null);
        }}
        parentId={null}
        selectedQuestion={selectedQuestion}
        onSubmit={handleBuilderSubmit}
        onSwitchToAI={() => {
          setIsBuilderOpen(false);
          setSelectedQuestion(null);
          setIsAIBuilderOpen(true);
        }}
      />

      <AIActivityQuestionsModal
        isOpen={isAIBuilderOpen}
        onClose={() => setIsAIBuilderOpen(false)}
        onSubmit={handleSaveQuestionAI}
        onAddToTask={async () => {
          await load();
          setIsAIBuilderOpen(false);
          setTab("list");
        }}
        onDeleteQuestion={handleDeleteGeneratedQuestionAI}
        parentId={null}
        resourcesParentId={defaultResourceParentId}
        resourceParentOptions={resourceParentOptions}
        resourceParentLabel="Apartado desde donde tomar recursos"
        onSwitchToManual={() => {
          setIsAIBuilderOpen(false);
          setIsBuilderOpen(true);
        }}
      />

      <CloneQuestionPicker
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        onConfirm={handlePickerConfirm}
        excludeIds={excludeIds}
        examId={exam.id}
        cursoId={cursoIdSafe}
        cursoTipo={cursoTipoSafe}
      />
    </div>
  );
}

/* ========================= Styles ========================= */

const CSS = `
:root{
  --paper:#ffffff;
  --text:#0f172a;
  --muted:#64748b;
  --muted2:#94a3b8;
  --border:#e5e7eb;
  --shadow: 0 22px 70px rgba(0,0,0,.35);
  --shadow2: 0 12px 28px rgba(2,6,23,.12);
  --danger:#ef4444;

  --blue:#2563eb;
  --blue2:#1d4ed8;
  --green:#10b981;
  --green2:#059669;
  --amber:#f59e0b;
  --amberText:#b45309;
}
`;

const ST = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(2,6,23,.62)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 16,
  },
  modal: {
    width: "min(1120px, 96vw)",
    maxHeight: "92vh",
    background: "var(--paper)",
    borderRadius: 18,
    overflow: "hidden",
    boxShadow: "var(--shadow)",
    border: "1px solid rgba(226,232,240,.85)",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    padding: "14px 16px",
    borderBottom: "1px solid var(--border)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    background: "linear-gradient(180deg, rgba(15,23,42,.02), rgba(15,23,42,0))",
  },
  headerLeft: { minWidth: 0 },
  headerRight: { display: "flex", alignItems: "center", gap: 10 },
  title: { fontWeight: 950, fontSize: 16, color: "var(--text)" },
  subtitle: {
    marginTop: 4,
    fontSize: 12.5,
    color: "var(--muted)",
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  examName: { fontWeight: 900, color: "var(--text)" },
  dot: { opacity: 0.6 },
  closeBtn: {
    border: "1px solid var(--border)",
    background: "#fff",
    padding: "8px 10px",
    borderRadius: 12,
    cursor: "pointer",
    fontWeight: 900,
  },

  tabsRow: {
    padding: "10px 16px",
    display: "flex",
    gap: 8,
    borderBottom: "1px solid var(--border)",
    background: "rgba(15,23,42,.01)",
  },
  tab: {
    padding: "9px 12px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,.10)",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 13,
    color: "#334155",
  },
  tabActive: {
    border: "1px solid rgba(37,99,235,.30)",
    background: "rgba(37,99,235,.08)",
    color: "#1d4ed8",
  },

  content: { padding: 16, overflow: "auto" },

  panel: {
    border: "1px solid rgba(226,232,240,.9)",
    borderRadius: 16,
    overflow: "hidden",
    background: "#fff",
    boxShadow: "0 10px 26px rgba(2,6,23,.06)",
  },
  panelHeader: {
    padding: 12,
    background: "rgba(15,23,42,.02)",
    borderBottom: "1px solid rgba(226,232,240,.9)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  badge: {
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(37,99,235,.08)",
    border: "1px solid rgba(37,99,235,.20)",
    color: "#1d4ed8",
    fontWeight: 900,
    fontSize: 12,
  },
  mutedSmall: { fontSize: 12.5, color: "var(--muted)" },

  table: {
    width: "100%",
    minWidth: 980,
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  th: {
    padding: 12,
    textAlign: "left",
    fontWeight: 950,
    fontSize: 12.5,
    color: "#334155",
    background: "rgba(15,23,42,.03)",
    borderBottom: "1px solid rgba(226,232,240,.9)",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  tr: { borderBottom: "1px solid rgba(226,232,240,.9)" },
  td: {
    padding: 12,
    verticalAlign: "top",
    borderBottom: "1px solid rgba(226,232,240,.9)",
  },

  qTopRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  qTitle: {
    fontWeight: 950,
    color: "var(--text)",
    lineHeight: 1.25,
    minWidth: 0,
    flex: 1,
  },
  qTitleCollapsed: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  qTitleExpanded: {
    whiteSpace: "normal",
    overflowWrap: "anywhere",   // ✅ rompe palabras largas
    wordBreak: "break-word",    // ✅ respaldo
  },
  linkBtn: {
    border: "1px solid rgba(37,99,235,.22)",
    background: "rgba(37,99,235,.08)",
    color: "#1d4ed8",
    padding: "6px 10px",
    borderRadius: 999,
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 12,
    whiteSpace: "nowrap",
    flex: "0 0 auto",
  },

  typePill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(37,99,235,.08)",
    border: "1px solid rgba(37,99,235,.18)",
    color: "#1d4ed8",
    fontWeight: 950,
    fontSize: 12.5,
    whiteSpace: "nowrap",
    justifyContent: "center",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  typePillPool: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(245,158,11,.14)",
    border: "1px solid rgba(245,158,11,.25)",
    color: "var(--amberText)",
    fontWeight: 950,
    fontSize: 12.5,
    whiteSpace: "nowrap",
    justifyContent: "center",
  },

  qtyPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 46,
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(15,23,42,.06)",
    border: "1px solid rgba(15,23,42,.10)",
    color: "#334155",
    fontWeight: 950,
    fontSize: 12.5,
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  originPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(16,185,129,.10)",
    border: "1px solid rgba(16,185,129,.18)",
    color: "#047857",
    fontWeight: 950,
    fontSize: 12.5,
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  originHint: { marginTop: 6, fontSize: 12, color: "var(--muted)" },

  actions: { display: "inline-flex", gap: 8, flexWrap: "wrap", justifyContent: "center" },
  btnEdit: {
    border: "1px solid rgba(37,99,235,.35)",
    color: "#1d4ed8",
    background: "rgba(37,99,235,.10)",
    padding: "7px 10px",
    borderRadius: 12,
    fontSize: 12.5,
    fontWeight: 950,
    cursor: "pointer",
  },
  btnDelete: {
    border: "1px solid rgba(239,68,68,.35)",
    color: "#b91c1c",
    background: "rgba(239,68,68,.10)",
    padding: "7px 10px",
    borderRadius: 12,
    fontSize: 12.5,
    fontWeight: 950,
    cursor: "pointer",
  },
  btnWarn: {
    border: "1px solid rgba(245,158,11,.35)",
    color: "var(--amberText)",
    background: "rgba(245,158,11,.14)",
    padding: "7px 10px",
    borderRadius: 12,
    fontSize: 12.5,
    fontWeight: 950,
    cursor: "pointer",
  },

  emptyCell: {
    padding: 18,
    textAlign: "center",
    color: "var(--muted)",
    background: "rgba(15,23,42,.01)",
  },

  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 16,
  },
  card: {
    border: "1px solid rgba(226,232,240,.9)",
    borderRadius: 16,
    padding: 16,
    background: "#fff",
    boxShadow: "0 10px 26px rgba(2,6,23,.06)",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  cardTitle: { fontWeight: 950, color: "var(--text)", fontSize: 14 },
  cardDesc: { color: "var(--muted)", fontSize: 13, lineHeight: 1.35 },

  primaryGreen: {
    background: "linear-gradient(135deg, var(--green), var(--green2))",
    color: "#fff",
    border: "none",
    padding: "10px 14px",
    borderRadius: 14,
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "var(--shadow2)",
    width: "fit-content",
  },
  primaryBlue: {
    background: "linear-gradient(135deg, var(--blue), var(--blue2))",
    color: "#fff",
    border: "none",
    padding: "10px 14px",
    borderRadius: 14,
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "var(--shadow2)",
    width: "fit-content",
  },
  primaryPurple: {
    background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
    color: "#fff",
    border: "none",
    padding: "10px 14px",
    borderRadius: 14,
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "var(--shadow2)",
    width: "fit-content",
  },
  inlineActions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center",
  },

  // option preview
  optsWrap: { display: "flex", gap: 8, flexWrap: "wrap" },
  optPill: {
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(15,23,42,.05)",
    border: "1px solid rgba(15,23,42,.10)",
    color: "#334155",
    fontWeight: 900,
    fontSize: 12.5,

    maxWidth: "100%",          // ✅ no empuja a la derecha
    whiteSpace: "normal",      // ✅ permite varias líneas
    overflowWrap: "anywhere",  // ✅ rompe palabras largas
    wordBreak: "break-word",
  },
  optPillOk: {
    background: "rgba(16,185,129,.12)",
    border: "1px solid rgba(16,185,129,.20)",
    color: "#047857",
  },
  morePill: {
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(37,99,235,.08)",
    border: "1px solid rgba(37,99,235,.18)",
    color: "#1d4ed8",
    fontWeight: 950,
    fontSize: 12.5,

    maxWidth: "100%",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  answerBox: {
    marginTop: 10,
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(226,232,240,.9)",
    background: "rgba(15,23,42,.02)",
    display: "flex",
    gap: 10,
    alignItems: "baseline",
    flexWrap: "wrap",
  },
  answerLabel: { fontSize: 12, fontWeight: 950, color: "#475569" },
  answerValue: {
    fontSize: 12.5,
    fontWeight: 800,
    color: "var(--text)",

    maxWidth: "100%",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },

  explBox: {
    marginTop: 10,
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(226,232,240,.9)",
    background: "rgba(15,23,42,.02)",
  },
  explLabel: { fontSize: 12, fontWeight: 950, color: "#475569" },
  explText: {
    marginTop: 6,
    fontSize: 12.5,
    color: "#334155",
    lineHeight: 1.35,

    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },

  listTools: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  searchWrap: {
    position: "relative",
    minWidth: 320,
    flex: "1 1 380px",
    maxWidth: 520,
  },
  searchIcon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: 14,
    opacity: 0.85,
  },
  searchInput: {
    width: "100%",
    padding: "10px 38px 10px 36px",
    borderRadius: 999,
    border: "1px solid rgba(226,232,240,.95)",
    outline: "none",
    fontWeight: 800,
    fontSize: 13,
    color: "var(--text)",
    background: "#fff",
  },
  searchClear: {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    width: 30,
    height: 30,
    borderRadius: 999,
    border: "1px solid rgba(226,232,240,.95)",
    background: "rgba(15,23,42,.04)",
    cursor: "pointer",
    fontWeight: 950,
    color: "#64748b",
  },
  pageSizeWrap: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid rgba(226,232,240,.95)",
    background: "rgba(15,23,42,.02)",
  },
  pageSizeSelect: {
    border: "1px solid rgba(226,232,240,.95)",
    borderRadius: 999,
    padding: "8px 10px",
    fontWeight: 950,
    fontSize: 12.5,
    outline: "none",
    background: "#fff",
    cursor: "pointer",
  },

  paginationBar: {
    padding: 12,
    borderTop: "1px solid rgba(226,232,240,.9)",
    background: "rgba(15,23,42,.01)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  pagerBtns: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  pagerBtn: {
    padding: "8px 12px",
    borderRadius: 12,
    border: "1px solid rgba(226,232,240,.95)",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 950,
  },
  pagerBtnActive: {
    border: "1px solid rgba(37,99,235,.35)",
    background: "rgba(37,99,235,.10)",
    color: "#1d4ed8",
  },

  /*NUEVOOOOOOOOOOOOOOOOOOO*/

  heroHeader: {
    padding: "18px 18px 14px",
    borderBottom: "1px solid var(--border)",
    background: "#fbfbfc",
  },
  heroHeaderTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 14,
    flexWrap: "wrap",
  },
  heroIconWrap: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    minWidth: 0,
  },
  heroIcon: {
    width: 46,
    height: 46,
    borderRadius: 14,
    background: "#e0e7ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    flex: "0 0 auto",
  },
  heroMetaRow: {
    marginTop: 6,
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    color: "var(--muted)",
    fontSize: 13,
  },
  metaTimeChip: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 26,
    padding: "0 10px",
    borderRadius: 999,
    background: "#f3f4f6",
    border: "1px solid var(--border)",
    color: "var(--muted)",
    fontSize: 11,
    fontWeight: 900,
  },
  heroStatsRow: {
    marginTop: 8,
    display: "flex",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
    color: "var(--muted)",
    fontSize: 12.5,
  },
  heroMiniStat: {
    color: "var(--muted)",
  },
  closeGhostBtn: {
    border: "none",
    background: "transparent",
    color: "#9ca3af",
    fontSize: 22,
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: 10,
  },

  noticeBanner: {
    marginTop: 12,
    borderRadius: 16,
    padding: "12px 14px",
    display: "grid",
    gridTemplateColumns: "32px 1fr auto",
    gap: 12,
    alignItems: "flex-start",
    border: "1px solid transparent",
    boxShadow: "0 12px 28px rgba(15,23,42,.08)",
  },

  noticeInfo: {
    background: "#eff6ff",
    borderColor: "#bfdbfe",
    color: "#1d4ed8",
  },

  noticeSuccess: {
    background: "#ecfdf5",
    borderColor: "#bbf7d0",
    color: "#047857",
  },

  noticeWarning: {
    background: "#fff7ed",
    borderColor: "#fdba74",
    color: "#c2410c",
  },

  noticeError: {
    background: "#fef2f2",
    borderColor: "#fecaca",
    color: "#b91c1c",
  },

  noticeIcon: {
    width: 32,
    height: 32,
    borderRadius: 999,
    background: "rgba(255,255,255,.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    flex: "0 0 auto",
  },

  noticeBody: {
    minWidth: 0,
  },

  noticeTitle: {
    fontWeight: 1000,
    fontSize: 14,
    lineHeight: 1.25,
  },

  noticeText: {
    marginTop: 4,
    fontSize: 13,
    lineHeight: 1.4,
    fontWeight: 700,
  },

  noticeList: {
    margin: "8px 0 0",
    paddingLeft: 18,
    fontSize: 12.5,
    lineHeight: 1.45,
    fontWeight: 700,
  },

  noticeClose: {
    width: 28,
    height: 28,
    borderRadius: 10,
    border: "1px solid rgba(15,23,42,.10)",
    background: "rgba(255,255,255,.7)",
    color: "inherit",
    cursor: "pointer",
    fontWeight: 1000,
  },
  warningBanner: {
    marginTop: 16,
    border: "1px solid #c7d2fe",
    background: "#eef2ff",
    borderRadius: 14,
    padding: "12px 14px",
  },
  warningBannerTitle: {
    color: "#4338ca",
    fontWeight: 1000,
    fontSize: 14,
  },
  warningBannerText: {
    color: "#4f46e5",
    fontSize: 13,
    marginTop: 4,
  },
  lockBanner: {
    marginTop: 12,
    border: "1px solid rgba(245,158,11,.35)",
    background: "rgba(245,158,11,.12)",
    borderRadius: 14,
    padding: "10px 12px",
    color: "#92400e",
    fontWeight: 900,
    fontSize: 13,
  },
  cleanTabsRow: {
    padding: "0 18px",
    minHeight: 50,
    borderBottom: "1px solid var(--border)",
    display: "flex",
    alignItems: "center",
    gap: 18,
    background: "#fff",
  },
  cleanTab: {
    minHeight: 50,

    // ✅ Sin shorthand: nada de border, borderBottom ni borderBottomColor mezclado.
    borderTopWidth: 0,
    borderTopStyle: "solid",
    borderTopColor: "transparent",

    borderRightWidth: 0,
    borderRightStyle: "solid",
    borderRightColor: "transparent",

    borderBottomWidth: 2,
    borderBottomStyle: "solid",
    borderBottomColor: "transparent",

    borderLeftWidth: 0,
    borderLeftStyle: "solid",
    borderLeftColor: "transparent",

    background: "transparent",
    color: "#6b7280",
    fontSize: 14,
    fontWeight: 1000,
    cursor: "pointer",
    position: "relative",
    padding: "0 2px",
  },
  cleanTabActive: {
    color: "#4f46e5",

    // ✅ Misma propiedad específica que ya existe en cleanTab.
    borderBottomWidth: 2,
    borderBottomStyle: "solid",
    borderBottomColor: "#4f46e5",
  },
  panelHeaderRedesigned: {
    padding: 14,
    background: "rgba(15,23,42,.02)",
    borderBottom: "1px solid rgba(226,232,240,.9)",
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 12,
    alignItems: "center",
  },
  searchWrapWide: {
    position: "relative",
    minWidth: 0,
  },
  searchInputWide: {
    width: "100%",
    minHeight: 42,
    border: "1px solid var(--border)",
    borderRadius: 14,
    padding: "10px 14px 10px 38px",
    fontSize: 14,
    outline: "none",
    background: "#fff",
  },
  filterSelect: {
    minHeight: 42,
    border: "1px solid var(--border)",
    borderRadius: 14,
    padding: "10px 12px",
    fontSize: 14,
    fontWeight: 700,
    background: "#fff",
  },
  typeHint: {
    marginTop: 6,
    color: "var(--muted)",
    fontSize: 11,
  },
  originPillTable: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px 10px",
    borderRadius: 8,
    fontWeight: 1000,
    fontSize: 11,
    border: "1px solid transparent",
  },
  originAi: {
    background: "#f5ebff",
    color: "#9333ea",
    borderColor: "#e9d5ff",
  },
  originBanco: {
    background: "#ecfeff",
    color: "#0f766e",
    borderColor: "#a5f3fc",
  },
  originArchivo: {
    background: "#fff7ed",
    color: "#c2410c",
    borderColor: "#fdba74",
  },
  originManual: {
    background: "#eff6ff",
    color: "#2563eb",
    borderColor: "#bfdbfe",
  },
  iconActions: {
    display: "inline-flex",
    gap: 8,
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  iconBtn: {
    width: 32,
    height: 32,
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "#fff",
    cursor: "pointer",
  },
  iconBtnDanger: {
    width: 32,
    height: 32,
    borderRadius: 10,
    border: "1px solid #fecaca",
    background: "#fff",
    color: "#b91c1c",
    cursor: "pointer",
  },
  addGridRedesigned: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 14,
  },
  addCard: {
    border: "1px solid rgba(226,232,240,.9)",
    borderRadius: 18,
    padding: 18,
    background: "#fff",
    boxShadow: "0 10px 26px rgba(2,6,23,.05)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 12,
  },
  addCardIconBlue: {
    width: 48,
    height: 48,
    borderRadius: 999,
    background: "#eaf2ff",
    color: "#2563eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
  },
  addCardIconPurple: {
    width: 48,
    height: 48,
    borderRadius: 999,
    background: "#f5ebff",
    color: "#9333ea",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
  },
  addCardIconGreen: {
    width: 48,
    height: 48,
    borderRadius: 999,
    background: "#eafaf5",
    color: "#0f766e",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
  },
  addCardIconOrange: {
    width: 48,
    height: 48,
    borderRadius: 999,
    background: "#fff4ea",
    color: "#ea580c",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
  },
  addCardTitle: {
    fontWeight: 1000,
    color: "var(--text)",
    fontSize: 15,
  },
  addCardDesc: {
    color: "var(--muted)",
    fontSize: 13,
    lineHeight: 1.45,
    maxWidth: 320,
  },
  addCardNote: {
    color: "#ea580c",
    fontWeight: 900,
    fontSize: 12,
  },
  addCardBtnBlue: {
    width: "100%",
    minHeight: 42,
    borderRadius: 12,
    border: "none",
    background: "#eaf2ff",
    color: "#2563eb",
    fontWeight: 1000,
    cursor: "pointer",
  },
  addCardBtnPurple: {
    width: "100%",
    minHeight: 42,
    borderRadius: 12,
    border: "none",
    background: "#f5ebff",
    color: "#9333ea",
    fontWeight: 1000,
    cursor: "pointer",
  },
  addCardBtnGreen: {
    width: "100%",
    minHeight: 42,
    borderRadius: 12,
    border: "none",
    background: "#eafaf5",
    color: "#0f766e",
    fontWeight: 1000,
    cursor: "pointer",
  },
  addCardBtnOrange: {
    width: "100%",
    minHeight: 42,
    borderRadius: 12,
    border: "none",
    background: "#fff4ea",
    color: "#ea580c",
    fontWeight: 1000,
    cursor: "pointer",
  },

};
